import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ResizableTable from '../../common/ResizableTable';
import adSettingsApi from '../../../api/watconnect/AdSettingsApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import { formatDateTime } from '../../../utils/formatUtils';
import FieldMappingEditor, { UniqueFieldUpsertHint } from './FieldMappingEditor';
import {
  buildDefaultMappingRows,
  buildMappingPayload,
  formatMappingSummary,
  parseStoredMapping,
} from './fieldMappingUtils';

function cell(value, displayValue) {
  const v = value ?? '';
  const d = displayValue ?? v;
  return {
    value: v,
    displayValue: d === '' ? '—' : String(d),
  };
}

const WatconnectAdSettings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 20;

  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [recordId, setRecordId] = useState('');
  const [adsSetting, setAdsSetting] = useState(null);
  const [leadFields, setLeadFields] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [mappingRows, setMappingRows] = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState(null);
  const [isEditingMapping, setIsEditingMapping] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adSettingsApi.list();
      if (res.data?.success) {
        setRows(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setRows([]);
        setError(res.data?.message || 'Failed to load ads settings');
      }
    } catch (err) {
      console.error('Error loading ads settings:', err);
      setRows([]);
      setError(
        err.response?.data?.message || err.message || 'Failed to load ads settings'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadRows();
  }, [loadRows]);

  const loadMappingContext = useCallback(async (existingRecord) => {
    setMappingLoading(true);
    setQuestionsError(null);
    try {
      let record = existingRecord;
      if (!record) {
        const currentRes = await adSettingsApi.fetchCurrent();
        if (currentRes.data?.success && currentRes.data.data) {
          record = currentRes.data.data;
        }
      }

      if (!record?.page_id || !record?.form_id) {
        setQuestionsError(
          'No watconnect_ads_setting with page_id and form_id found.'
        );
        setMappingRows(buildDefaultMappingRows([]));
        setAdsSetting(null);
        return;
      }

      setAdsSetting(record);
      if (record.id) {
        setRecordId(record.id);
      }

      const [questionsRes, leadConfigRes] = await Promise.all([
        adSettingsApi.fetchFormQuestions({
          adsSettingId: record.id,
          page_id: record.page_id,
          form_id: record.form_id,
        }),
        ObjectConfigApi.fetchConfigObjectByNameWithFields('lead'),
      ]);

      const fields = leadConfigRes?.data?.fields || [];
      const editableFields = fields.filter(
        (field) =>
          field.is_deleted !== true &&
          field.is_readonly !== true &&
          !['id', 'createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid'].includes(
            String(field.field_name || field.fieldName || '').toLowerCase()
          )
      );
      setLeadFields(editableFields);

      if (questionsRes.data?.success) {
        setQuestions(Array.isArray(questionsRes.data.questions) ? questionsRes.data.questions : []);
      } else {
        setQuestions([]);
        setQuestionsError(
          questionsRes.data?.message || 'Failed to load Facebook form questions'
        );
      }

      setMappingRows(
        parseStoredMapping(existingRecord?.field_mapping, editableFields)
      );
    } catch (err) {
      console.error('Error loading mapping context:', err);
      setQuestionsError(
        err.response?.data?.message || err.message || 'Failed to load mapping data'
      );
      setMappingRows(buildDefaultMappingRows([]));
    } finally {
      setMappingLoading(false);
    }
  }, []);

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const mappingText = formatMappingSummary(row.field_mapping).toLowerCase();
      return (
        String(row.page_id || '').toLowerCase().includes(q) ||
        String(row.form_id || '').toLowerCase().includes(q) ||
        mappingText.includes(q)
      );
    });
  }, [rows, searchTerm]);

  const totalRecords = filteredRows.length;
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return filteredRows.slice(start, start + recordsPerPage);
  }, [filteredRows, currentPage]);

  const tableData = useMemo(
    () =>
      paginatedRows.map((row) => ({
        id: row.id,
        __raw: row,
        page_id: cell(row.page_id),
        form_id: cell(row.form_id),
        field_mapping: cell(
          formatMappingSummary(row.field_mapping),
          formatMappingSummary(row.field_mapping)
        ),
        lastmodifieddate: cell(
          row.lastmodifieddate,
          row.lastmodifieddate ? formatDateTime(row.lastmodifieddate) : '—'
        ),
      })),
    [paginatedRows]
  );

  const columns = useMemo(
    () => [
      { field: 'page_id', label: 'Page ID', sortable: true },
      { field: 'form_id', label: 'Form ID', sortable: true },
      { field: 'field_mapping', label: 'Field mapping', sortable: true },
      { field: 'lastmodifieddate', label: 'Last modified', sortable: true },
    ],
    []
  );

  const openCreateModal = async () => {
    setRecordId('');
    setIsEditingMapping(true);
    setShowFormModal(true);
    await loadMappingContext(null);
  };

  const openEditModal = async (record) => {
    const raw = record.__raw || record;
    setRecordId(raw.id || '');
    setIsEditingMapping(false);
    setShowFormModal(true);
    await loadMappingContext(raw);
  };

  const openDeleteModal = (record) => {
    setDeleteTarget(record.__raw || record);
    setShowDeleteModal(true);
  };

  const handleCancelEdit = () => {
    setMappingRows(parseStoredMapping(adsSetting?.field_mapping, leadFields));
    setIsEditingMapping(false);
  };

  const handleSave = async (e) => {
    e?.preventDefault();

    let fieldMapping;
    try {
      fieldMapping = buildMappingPayload(mappingRows);
    } catch (err) {
      toast.error(err.message);
      return;
    }

    const payload = {
      field_mapping: fieldMapping,
    };

    setSaving(true);
    try {
      const res = recordId
        ? await adSettingsApi.updateRecord(recordId, payload)
        : await adSettingsApi.createRecord(payload);

      if (res.data?.success) {
        toast.success(recordId ? 'Mapping saved.' : 'Ads settings created.');
        setIsEditingMapping(false);
        const saved = res.data.data;
        if (saved?.id) {
          setRecordId(saved.id);
          setAdsSetting(saved);
          setMappingRows(parseStoredMapping(saved.field_mapping, leadFields));
        }
        loadRows();
      } else {
        toast.error(res.data?.message || 'Save failed.');
      }
    } catch (err) {
      console.error('Save ads settings error:', err);
      toast.error(err.response?.data?.message || err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setSaving(true);
    try {
      const res = await adSettingsApi.deleteRecord(deleteTarget.id);
      if (res.data?.success) {
        toast.success('Ads settings deleted.');
        setShowDeleteModal(false);
        setDeleteTarget(null);
        loadRows();
      } else {
        toast.error(res.data?.message || 'Delete failed.');
      }
    } catch (err) {
      console.error('Delete ads settings error:', err);
      toast.error(
        err.response?.data?.message || err.message || 'Delete failed.'
      );
    } finally {
      setSaving(false);
    }
  };

  const actionsRenderer = (record) => {
    const raw = record.__raw || record;
    return (
      <div className="d-flex gap-1 flex-wrap">
        <Button
          size="sm"
          variant="outline-primary"
          title="View / edit mapping"
          onClick={(e) => {
            e.stopPropagation();
            openEditModal(record);
          }}
        >
          <i className="fa-regular fa-eye" /> View
        </Button>
        <Button
          size="sm"
          variant="outline-danger"
          title="Delete"
          onClick={(e) => {
            e.stopPropagation();
            openDeleteModal(raw);
          }}
        >
          <i className="fa-regular fa-trash-can" /> Delete
        </Button>
      </div>
    );
  };

  return (
    <Container fluid className="mt-4 px-4 pb-4">
      <Row className="mb-3">
        <Col>
          <h4 className="fw-semibold mb-1">Watconnect Ads Settings</h4>
          <p className="text-muted small mb-0">
            Map Meta Lead Ad form questions to CRM lead fields to create or upsert leads
            automatically.
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-3">
          <ResizableTable
            title="Ads settings"
            data={tableData}
            columns={columns}
            loading={loading}
            error={error}
            searchTerm={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            currentPage={currentPage}
            totalRecords={totalRecords}
            recordsPerPage={recordsPerPage}
            onPageChange={setCurrentPage}
            showSerialNumber
            showActions
            actionsRenderer={actionsRenderer}
            showNewButton
            onNewRecord={openCreateModal}
            showPagination={totalRecords > recordsPerPage}
            searchPlaceholder="Search page ID, form ID, mapping…"
            emptyMessage="No ads settings found. Click New to add one."
            onRefresh={loadRows}
            exportFilename="watconnect_ads_setting"
          />
        </Card.Body>
      </Card>

      <Modal
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
        centered
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {recordId
              ? isEditingMapping
                ? 'Edit question mapping'
                : 'View question mapping'
              : 'New question mapping'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mappingLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading mapping…
            </div>
          ) : (
            <>
              {adsSetting && (
                <Row className="mb-3 g-3">
                  <Col md={6}>
                    <div className="small text-muted">Page ID</div>
                    <div className="fw-semibold">{adsSetting.page_id || '—'}</div>
                  </Col>
                  <Col md={6}>
                    <div className="small text-muted">Form ID</div>
                    <div className="fw-semibold">{adsSetting.form_id || '—'}</div>
                  </Col>
                </Row>
              )}

              <FieldMappingEditor
                rows={mappingRows}
                onRowsChange={setMappingRows}
                questions={questions}
                leadFields={leadFields}
                loadingQuestions={mappingLoading}
                questionsError={questionsError}
                readOnly={!isEditingMapping}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="flex-column align-items-stretch gap-2">
          {!mappingLoading && isEditingMapping ? <UniqueFieldUpsertHint /> : null}
          <div className="d-flex justify-content-end gap-2 w-100">
            <Button variant="light" onClick={() => setShowFormModal(false)}>
              Close
            </Button>
            {!mappingLoading && !isEditingMapping && recordId ? (
              <Button variant="primary" onClick={() => setIsEditingMapping(true)}>
                <i className="fas fa-edit me-1 text-white" aria-hidden />
                Edit Mapping
              </Button>
            ) : null}
            {!mappingLoading && isEditingMapping ? (
              <>
                {recordId ? (
                  <Button variant="light" onClick={handleCancelEdit} disabled={saving}>
                    Cancel
                  </Button>
                ) : null}
                <Button
                  variant="success"
                  onClick={handleSave}
                  disabled={saving || mappingLoading}
                >
                  {saving ? 'Saving…' : 'Save Mapping'}
                </Button>
              </>
            ) : null}
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete ads settings?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Remove settings for page{' '}
          <strong>{deleteTarget?.page_id}</strong> / form{' '}
          <strong>{deleteTarget?.form_id}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </Container>
  );
};

export default WatconnectAdSettings;
